import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { SizeCalculationMaleParams, SizeMaleSuggestion } from '@/types/size'
import { zodResolver } from '@hookform/resolvers/zod'
import { UseMutateFunction } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { Dispatch, SetStateAction } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

interface SizeGuideMaleContentProps {
  height: number
  weight: number
  setHeight: Dispatch<SetStateAction<number>>
  setWeight: Dispatch<SetStateAction<number>>
  recommendedSize?: SizeMaleSuggestion | null
  isLoading?: boolean
  sizeType?: string
  productId?: string
  calculateSize: UseMutateFunction<
    SizeMaleSuggestion | null,
    Error,
    SizeCalculationMaleParams,
    unknown
  >
  shapeType?: {
    value: string
    label: string
    is_custom: boolean
  }
}

export const SizeGuideMaleContent = ({
  height,
  weight,
  setHeight,
  setWeight,
  recommendedSize,
  isLoading = false,
  sizeType,
  productId,
  calculateSize,
  shapeType,
}: SizeGuideMaleContentProps) => {
  const t = useTranslations('product_detail.instruction_size')
  const tBtnSize = useTranslations('product_detail.size_chart.calculate_size')

  const FormSchema = z.object({
    height: z
      .number({
        required_error: tBtnSize('validation.required_error'),
        invalid_type_error: tBtnSize('validation.invalid_type_error'),
      })
      .min(155, {
        message: tBtnSize('validation.range_error', {
          min: 155,
          max: 190,
          unit: t('form.unit.cm'),
        }),
      })
      .max(190, {
        message: tBtnSize('validation.range_error', {
          min: 155,
          max: 190,
          unit: t('form.unit.cm'),
        }),
      }),
    weight: z
      .number({
        required_error: tBtnSize('validation.required_error'),
        invalid_type_error: tBtnSize('validation.invalid_type_error'),
      })
      .min(40, {
        message: tBtnSize('validation.range_error', {
          min: 40,
          max: 90,
          unit: t('form.unit.kg'),
        }),
      })
      .max(90, {
        message: tBtnSize('validation.range_error', {
          min: 40,
          max: 90,
          unit: t('form.unit.kg'),
        }),
      }),
  })

  type FormValues = z.infer<typeof FormSchema>

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    mode: 'onChange',
    defaultValues: {
      height,
      weight,
    },
  })

  const onSubmit = (values: FormValues) => {
    if (sizeType) {
      calculateSize({
        height: values.height,
        weight: values.weight,
        type: sizeType,
        product_id: productId || '',
      })
    }
  }

  return (
    <div>
      {shapeType?.label  && shapeType?.label !== '(Không thiết lập)' ? (
        <div className='mb-2 rounded bg-neutral-100 px-4 py-3 text-xs md:text-sm'>
          {t('shape_type_label')}{' '}
          <span className='text-sm font-medium md:text-base'>
            {shapeType.label}
          </span>
        </div>
      ) : null}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-3'>
          <div className='flex items-start gap-6'>
            <FormField
              control={form.control}
              name='height'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>{t('form.height')}</FormLabel>
                  <FormControl>
                    <div className='relative w-full'>
                      <Input
                        {...field}
                        inputMode='numeric'
                        className='rounded-lg border-neutral-300 px-4 py-3'
                        placeholder={t('form.placeholder.height')}
                        suffix={
                          <p className='font-sans text-sm font-normal leading-5 text-neutral-300'>
                            {t('form.unit.cm')}
                          </p>
                        }
                        min={155}
                        max={190}
                        onChange={(e) => {
                          const rawValue = e.target.value
                          if (rawValue === '') {
                            field.onChange('')
                            return
                          }

                          let value = parseInt(rawValue, 10)
                          if (!isNaN(value)) {
                            if (value > 190) {
                              value = 190
                            }
                            field.onChange(value)
                            setHeight(value)
                          }
                        }}
                        onBlur={(e) => {
                          const rawValue = e.target.value
                          let value = parseInt(rawValue, 10)

                          if (isNaN(value) || value < 155) {
                            value = 155
                          }

                          field.onChange(value)
                          setHeight(value)
                        }}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='weight'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>{t('form.weight')}</FormLabel>
                  <FormControl>
                    <div className='relative w-full'>
                      <Input
                        {...field}
                        inputMode='numeric'
                        className='rounded-lg border-neutral-300 px-4 py-3'
                        placeholder={t('form.placeholder.weight')}
                        suffix={
                          <p className='font-sans text-sm font-normal leading-5 text-neutral-300'>
                            {t('form.unit.kg')}
                          </p>
                        }
                        min={40}
                        max={90}
                        onChange={(e) => {
                          const rawValue = e.target.value
                          if (rawValue === '') {
                            field.onChange('')
                            return
                          }

                          let value = parseInt(rawValue, 10)
                          if (!isNaN(value)) {
                            if (value > 90) {
                              value = 90
                            }
                            field.onChange(value)
                            setWeight(value)
                          }
                        }}
                        onBlur={(e) => {
                          const rawValue = e.target.value
                          let value = parseInt(rawValue, 10)

                          if (isNaN(value) || value < 40) {
                            value = 40
                          }

                          field.onChange(value)
                          setWeight(value)
                        }}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Button
            variant='black'
            type='submit'
            className='mt-3'
            disabled={isLoading}
          >
            {isLoading
              ? tBtnSize('button.calculating')
              : tBtnSize('button.calculate')}
          </Button>
        </form>
      </Form>

      {recommendedSize &&
        (recommendedSize.size && recommendedSize.size !== 'Không tìm thấy' ? (
          <div className='mb-5 md:mb-10'>
            <p className='mt-3 rounded bg-secondary-50 px-4 py-3 text-base leading-5 text-neutral-800 opacity-90'>
              {tBtnSize.rich('result.male_success', {
                size: recommendedSize.size,
                strong: (chunks) => <strong>{chunks}</strong>,
              })}
            </p>
            {recommendedSize.note ? (
              <p
                className='mt-3 rounded bg-neutral-200 px-4 py-3 text-base leading-5 text-neutral-800 opacity-90'
                dangerouslySetInnerHTML={{ __html: recommendedSize.note }}
              />
            ) : null}
          </div>
        ) : (
          <p className='mt-3 rounded bg-secondary-50 px-4 py-3 text-base leading-5 text-neutral-800 opacity-90'>
            {tBtnSize('result.not_found')}
          </p>
        ))}
    </div>
  )
}
